import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';
import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { userGetCurrent } from '@uncinc/react-drupal-core';

import { webformSubmit } from '../../store/actions/Webform';

import {
  Button,
  CoreLink,
  Main,
  ToggleGroup,
} from '../../components';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';
import useTypedDispatch from '../../hooks/useTypedDispatch';
import { isEligibleForResearch, screeningQuestions } from '../../config/aidaScreeningQuestions';
import type { AidaAnswers } from '../OnboardingAida';

import './style.scss';

type LocationState = { answers?: AidaAnswers };

const PageOnboardingAidaConsent: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { state: routeState } = useLocation<LocationState>();
  const { logout, joyride } = useRoutes();
  const { buildClass, joinClasses } = useClasses('PageOnboardingAidaConsent', 'PageOnboardingAidaConsent--default');

  const dispatch = useTypedDispatch();
  const [consent, setConsent] = useState<string | undefined>(undefined);
  const [showError, setShowError] = useState(false);

  const currentUserId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], 0));
  const userEmail = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['user', currentUserId, 'mail', 0, 'value'], ''));

  useEffect(() => {
    const answers = routeState?.answers;
    if (!answers || !isEligibleForResearch(answers)) {
      history.replace(joyride);
    }
  }, [history, joyride, routeState]);

  const handleSubmit = () => {
    if (consent === undefined) {
      setShowError(true);
      return;
    }

    const answers = routeState?.answers ?? {};
    const screeningQuestionIds = screeningQuestions.map(({ id }) => id);
    const values: Record<string, boolean | number> = {
      consent: consent === 'yes',
    };
    Object.entries(answers).forEach(([k, v]) => {
      const id = Number(k);
      values[`q${id}`] = screeningQuestionIds.includes(id) ? Boolean(v) : v;
    });
    dispatch(webformSubmit('aida_screening', values))
      .then(() => dispatch(userGetCurrent()))
      .catch(() => {})
      .finally(() => history.push(joyride));
  };

  return (
    <Page
      className={buildClass()}
      hideMenuMain
      hideChrome
      title={t('aida_consent.page_title')}
      headerContent={(
        <CoreLink
          to={logout}
          title={t('buttons.logout')}
          className={buildClass('header-logout')}
        />
      )}
    >
      <Container>
        <div className="row">
          <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-4 py-md-12">
            <Main className={buildClass('content')}>
              <h1 className={buildClass('title')}>{t('aida_consent.title')}</h1>
              <p className={joinClasses(buildClass('body'), 'body--large')}>
                {t('aida_consent.body')}
              </p>

              <ToggleGroup
                id="aida-consent"
                label={t('aida_consent.question')}
                type="binary"
                options={[
                  { label: t('aida_screening.options.yes'), value: 'yes' },
                  { label: t('aida_screening.options.no'), value: 'no' },
                ]}
                selectedValue={consent}
                onChange={(val) => {
                  setConsent(val);
                  setShowError(false);
                }}
                error={showError ? t('aida_consent.validation') : undefined}
              />

              {consent === 'yes' && (
                <p className={buildClass('consent-given')}>
                  <Trans
                    i18nKey="aida_consent.consent_given"
                    values={{ email: userEmail }}
                    components={{ bold: <strong /> }}
                  />
                </p>
              )}

              <div className={buildClass('submit')}>
                <Button onClick={handleSubmit}>
                  {t('aida_consent.submit')}
                </Button>
              </div>
            </Main>
          </div>
        </div>
      </Container>
    </Page>
  );
};

export default PageOnboardingAidaConsent;
